import { APP_URL } from "../../config";
import { TEAMS_COURSE_CARD_TEMPLATE, TEAMS_EMPTY_STATE_TEMPLATE } from "../../themes/PresetTeamsTemplate";

/**
 * Processes the Teams HTML template and replaces all placeholders
 * @param {string} template - The HTML template
 * @param {Object} data - The data to use for replacements
 * @returns {string} - The processed HTML
 */
const processTeamsTemplate = (template, data) => {
    if (!template) return '';

    const {
        colors,
        logo,
        logoWhiteBackground,
        companyName,
        headerTitle,
        headerSubtitle,
        userName,
        greetingMessage,
        sectionTitle,
        footerText,
        courses = [],
        pageTitle,
    } = data;

    let processedTemplate = template;

    // Default values
    const defaultValues = {
        pageTitle: "Your Training Courses",
        headerTitle: "Training Portal",
        headerSubtitle: "Security Awareness Training",
        userName: "User",
        greetingMessage: "You have pending training courses to complete.",
        sectionTitle: "Your Courses",
        footerText: "Powered by Boxphish Security Awareness",
        companyName: "Your Company"
    };

    // Helper function to get value or default
    const getValue = (value, defaultValue) => {
        return value !== undefined && value !== null && value !== '' ? value : defaultValue;
    };

    // Replace color placeholders
    const primaryColor = colors?.primary || '#6264a7';
    processedTemplate = processedTemplate
        .replace(/{color\.primary}/g, primaryColor)
        .replace(/{color\.secondary}/g, colors?.secondary || primaryColor);

    // Replace logo
    const logoSizeVal = data.logoSize || 48;
    const logoBgColor = logoWhiteBackground ? '#ffffff' : 'transparent';
    if (logo) {
        processedTemplate = processedTemplate
            .replace(/{client\.logo}/g, `${APP_URL}/${logo}`)
            .replace(/{logo\.size}/g, logoSizeVal)
            .replace(/{logo\.background}/g, logoBgColor);
    } else {
        // Default placeholder logo
        processedTemplate = processedTemplate
            .replace(/{client\.logo}/g, 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjNjI2NGE3Ij48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptMCAzYzEuNjYgMCAzIDEuMzQgMyAzcy0xLjM0IDMtMyAzLTMtMS4zNC0zLTMgMS4zNC0zIDMtM3ptMCAxNC4yYy0yLjUgMC00LjcxLTEuMjgtNi0zLjIyLjAzLTEuOTkgNC0zLjA4IDYtMy4wOCAxLjk5IDAgNS45NyAxLjA5IDYgMy4wOC0xLjI5IDEuOTQtMy41IDMuMjItNiAzLjIyeiIvPjwvc3ZnPg==')
            .replace(/{logo\.size}/g, logoSizeVal)
            .replace(/{logo\.background}/g, logoBgColor);
    }

    // Replace text placeholders
    processedTemplate = processedTemplate
        .replace(/{page\.title}/g, getValue(pageTitle, defaultValues.pageTitle))
        .replace(/{header\.title}/g, getValue(headerTitle, defaultValues.headerTitle))
        .replace(/{header\.subtitle}/g, getValue(headerSubtitle, defaultValues.headerSubtitle))
        .replace(/{user\.name}/g, getValue(userName, defaultValues.userName))
        .replace(/{greeting\.message}/g, getValue(greetingMessage, defaultValues.greetingMessage))
        .replace(/{section\.title}/g, getValue(sectionTitle, defaultValues.sectionTitle))
        .replace(/{footer\.text}/g, getValue(footerText, defaultValues.footerText))
        .replace(/{company\.name}/g, getValue(companyName, defaultValues.companyName));

    // Generate course list HTML
    let courseListHtml = '';
    if (courses && courses.length > 0) {
        courseListHtml = courses.map(course => {
            let cardHtml = TEAMS_COURSE_CARD_TEMPLATE;

            // Determine status class
            let statusClass = 'status-pending';
            if (course.status?.toLowerCase() === 'overdue') {
                statusClass = 'status-overdue';
            } else if (course.status?.toLowerCase() === 'new') {
                statusClass = 'status-new';
            }

            // Remove status badge if no status provided
            if (!course.status) {
                cardHtml = cardHtml.replace(/<span class="course-status[^"]*">[^<]*<\/span>/g, '');
            }

            cardHtml = cardHtml
                .replace(/{course\.id}/g, course.id || '')
                .replace(/{course\.name}/g, course.name || 'Untitled Course')
                .replace(/{course\.dateAdded}/g, course.dateAdded || '')
                .replace(/{course\.status}/g, course.status || '')
                .replace(/{course\.statusClass}/g, statusClass);

            return cardHtml;
        }).join('');
    } else {
        courseListHtml = TEAMS_EMPTY_STATE_TEMPLATE;
    }

    // Pagination settings
    const coursesPerPage = 5;
    const totalPages = Math.ceil((courses?.length || 0) / coursesPerPage);
    const showPagination = (courses?.length || 0) > coursesPerPage;

    processedTemplate = processedTemplate
        .replace(/{course\.list}/g, courseListHtml)
        .replace(/{course\.count}/g, courses?.length || 0)
        .replace(/{courses\.json}/g, JSON.stringify(courses || []))
        .replace(/{pagination\.display}/g, showPagination ? 'flex' : 'none')
        .replace(/{pagination\.totalPages}/g, totalPages || 1)
        .replace(/{app\.url}/g, APP_URL || '');

    return processedTemplate;
};

export default processTeamsTemplate;
